import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export const TRACK_TABLE_VIEW_OPTIONS = ['performance', 'metadata'] as const;
type TrackTableViewOptions = typeof TRACK_TABLE_VIEW_OPTIONS;
export type TrackTableViewType = TrackTableViewOptions[number];

const QUERY_PARAM = 'table_view';
const DEFAULT_VIEW: TrackTableViewType = 'performance';

function isValidTrackTableViewValue(
  value: string,
): value is TrackTableViewType {
  return TRACK_TABLE_VIEW_OPTIONS.includes(value as TrackTableViewType);
}

export function useTrackTableViewUrlParam() {
  const [searchParams, setSearchParams] = useSearchParams();

  const trackTableViewValue = searchParams.get(QUERY_PARAM) || DEFAULT_VIEW;
  const trackTableView = isValidTrackTableViewValue(trackTableViewValue)
    ? trackTableViewValue
    : DEFAULT_VIEW;

  const setTrackTableView = useCallback(
    (view: TrackTableViewType) => {
      setSearchParams(
        (prevSearchParams) => {
          const newParams = new URLSearchParams(prevSearchParams);

          if (view === DEFAULT_VIEW) {
            newParams.delete(QUERY_PARAM);
          } else {
            newParams.set(QUERY_PARAM, view);
          }

          return newParams;
        },
        { preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!isValidTrackTableViewValue(trackTableViewValue)) {
      setTrackTableView(DEFAULT_VIEW);
    }
  }, [searchParams, setTrackTableView, trackTableViewValue]);

  return {
    trackTableView,
    setTrackTableView,
  };
}
